import { useEffect, useRef, useState } from 'react';
import { renderAsync } from 'docx-preview';
import { downloadAuthenticatedFile, fetchAuthenticatedFile } from '../services/apiClient';
import {
  formatAttachmentSize,
  getAttachmentName,
  getAttachmentUrl,
  inferAttachmentMimeType,
  isDocxMimeType,
  isImageMimeType,
  isLegacyDocMimeType,
  isPdfMimeType,
  isVideoMimeType,
  isWordMimeType,
} from '../utils/fileAttachmentUtils';

function FileActionButton({ children, className = '', disabled, onClick, variant = 'primary' }) {
  const variantClasses = {
    primary: 'bg-slate-600 text-white',
    secondary: 'bg-[#F8F8F8] text-slate-700',
    danger: 'bg-slate-700 text-white',
    accent: 'bg-slate-500 text-white',
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`rounded-[6px] px-3 py-2 text-[11px] font-bold disabled:opacity-60 ${variantClasses[variant]} ${className}`}
    >
      {children}
    </button>
  );
}

function DocxPreview({ blob }) {
  const containerRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    let cancelled = false;

    async function renderDocument() {
      if (!blob || !containerRef.current) {
        return;
      }

      setIsLoading(true);
      setErrorMessage('');
      containerRef.current.innerHTML = '';

      try {
        await renderAsync(blob, containerRef.current, undefined, {
          className: 'docx',
          inWrapper: true,
          ignoreWidth: true,
          breakPages: false,
        });

        if (!cancelled) {
          setIsLoading(false);
        }
      } catch (error) {
        if (!cancelled) {
          setErrorMessage('امکان نمایش این فایل ورد وجود ندارد. لطفاً دانلود کنید.');
          setIsLoading(false);
        }
      }
    }

    renderDocument();

    return () => {
      cancelled = true;
    };
  }, [blob]);

  return (
    <div className="mx-auto flex min-h-[50vh] max-w-4xl flex-col">
      {isLoading && (
        <p className="py-8 text-center text-[12px] font-semibold text-white/80">
          در حال آماده‌سازی پیش‌نمایش...
        </p>
      )}
      {errorMessage && (
        <p className="py-8 text-center text-[12px] font-semibold text-red-300">
          {errorMessage}
        </p>
      )}
      <div
        ref={containerRef}
        className="docx-preview-host overflow-auto rounded-[8px] bg-white p-3 text-slate-900"
      />
    </div>
  );
}

function FileViewerModal({ viewer, onClose }) {
  useEffect(() => {
    if (!viewer) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [viewer]);

  if (!viewer) {
    return null;
  }

  const { blob, blobUrl, fileName, mimeType } = viewer;

  return (
    <div className="fixed inset-0 z-[120] flex flex-col bg-black/95">
      <div className="flex items-center justify-between gap-3 px-4 py-3 text-white">
        <p className="truncate text-[12px] font-semibold">{fileName}</p>
        <button
          type="button"
          onClick={onClose}
          className="shrink-0 rounded-[6px] bg-white/15 px-3 py-1 text-[11px] font-bold"
        >
          بستن
        </button>
      </div>

      <div className="flex-1 overflow-auto px-3 pb-4">
        {isImageMimeType(mimeType) && (
          <img
            src={blobUrl}
            alt={fileName}
            className="mx-auto max-h-full w-full object-contain"
          />
        )}

        {isPdfMimeType(mimeType) && (
          <iframe
            title={fileName}
            src={blobUrl}
            className="h-[calc(100vh-120px)] w-full rounded-[8px] bg-white"
          />
        )}

        {isVideoMimeType(mimeType) && (
          <video
            src={blobUrl}
            controls
            playsInline
            className="mx-auto max-h-[calc(100vh-120px)] w-full"
          >
            مرورگر شما از پخش ویدیو پشتیبانی نمی‌کند.
          </video>
        )}

        {isDocxMimeType(mimeType) && blob && <DocxPreview blob={blob} />}

        {isLegacyDocMimeType(mimeType) && (
          <div className="mx-auto flex max-w-md flex-col items-center gap-3 rounded-[8px] bg-white px-6 py-10 text-center">
            <p className="text-[13px] font-bold text-slate-800">
              پیش‌نمایش فایل‌های DOC قدیمی پشتیبانی نمی‌شود
            </p>
            <p className="text-[11px] text-slate-600">
              لطفاً فایل را دانلود کنید. برای مشاهده آنلاین، فایل باید با فرمت DOCX باشد.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function getViewLabel(mimeType, isLoading) {
  if (isLoading) {
    return 'در حال بارگذاری...';
  }

  if (isPdfMimeType(mimeType)) {
    return 'مشاهده PDF';
  }

  if (isWordMimeType(mimeType)) {
    return 'مشاهده Word';
  }

  return 'مشاهده';
}

function canPreviewAttachment(mimeType) {
  return (
    isImageMimeType(mimeType) ||
    isPdfMimeType(mimeType) ||
    isVideoMimeType(mimeType) ||
    isWordMimeType(mimeType) ||
    Boolean(mimeType)
  );
}

function AttachmentItem({ attachment, index, isEmbedded, isLoading, onDownload, onView }) {
  const fileUrl = getAttachmentUrl(attachment);
  const fileName = getAttachmentName(attachment, index);
  const mimeType = inferAttachmentMimeType(attachment);
  const fileSize = formatAttachmentSize(attachment?.size);
  const containerClass = isEmbedded
    ? 'overflow-hidden rounded-[8px] bg-white'
    : 'overflow-hidden rounded-[8px] border border-slate-100 bg-[#F8F8F8]';
  const showPreviewButton = canPreviewAttachment(mimeType);

  if (isImageMimeType(mimeType) && fileUrl) {
    return (
      <div className={containerClass}>
        <button
          type="button"
          onClick={() => onView(attachment)}
          disabled={isLoading}
          className="block w-full"
        >
          <img
            src={fileUrl}
            alt={fileName}
            className="mx-auto max-h-[240px] w-full object-contain"
          />
        </button>

        <div className="flex flex-col gap-2 border-t border-slate-100 p-3">
          <p className="truncate text-[11px] font-semibold text-slate-700">{fileName}</p>
          {fileSize && <p className="text-[10px] text-slate-500">{fileSize}</p>}
          <div className="grid grid-cols-2 gap-2">
            <FileActionButton disabled={isLoading} onClick={() => onView(attachment)}>
              {getViewLabel(mimeType, isLoading)}
            </FileActionButton>
            <FileActionButton disabled={isLoading} onClick={() => onDownload(attachment)} variant="secondary">
              دانلود
            </FileActionButton>
          </div>
        </div>
      </div>
    );
  }

  if (isVideoMimeType(mimeType) && fileUrl) {
    return (
      <div className={containerClass}>
        <video
          src={fileUrl}
          controls
          playsInline
          className="mx-auto max-h-[240px] w-full bg-black"
        >
          مرورگر شما از پخش ویدیو پشتیبانی نمی‌کند.
        </video>

        <div className="flex flex-col gap-2 border-t border-slate-100 p-3">
          <p className="truncate text-[11px] font-semibold text-slate-700">{fileName}</p>
          {fileSize && <p className="text-[10px] text-slate-500">{fileSize}</p>}
          <div className="grid grid-cols-2 gap-2">
            <FileActionButton disabled={isLoading} onClick={() => onView(attachment)} variant="accent">
              {isLoading ? 'در حال بارگذاری...' : 'تمام‌صفحه'}
            </FileActionButton>
            <FileActionButton disabled={isLoading} onClick={() => onDownload(attachment)} variant="secondary">
              دانلود
            </FileActionButton>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={containerClass}>
      <div className="flex flex-col gap-2 p-3">
        <p className="truncate text-[11px] font-semibold text-slate-700">{fileName}</p>
        {fileSize && <p className="text-[10px] text-slate-500">{fileSize}</p>}
        <div className="grid grid-cols-2 gap-2">
          {showPreviewButton && (
            <FileActionButton
              disabled={isLoading}
              onClick={() => onView(attachment)}
              variant={isPdfMimeType(mimeType) || isWordMimeType(mimeType) ? 'danger' : 'primary'}
            >
              {getViewLabel(mimeType, isLoading)}
            </FileActionButton>
          )}
          <FileActionButton
            disabled={isLoading}
            onClick={() => onDownload(attachment)}
            variant="secondary"
            className={!showPreviewButton ? 'col-span-2' : ''}
          >
            دانلود
          </FileActionButton>
        </div>
      </div>
    </div>
  );
}

function FileAttachments({
  attachments = [],
  className = '',
  title = 'فایل‌های پیوست',
  variant = 'plain',
}) {
  const [viewer, setViewer] = useState(null);
  const [loadingAttachmentId, setLoadingAttachmentId] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const isEmbedded = variant === 'embedded';

  useEffect(() => {
    return () => {
      if (viewer?.blobUrl) {
        URL.revokeObjectURL(viewer.blobUrl);
      }
    };
  }, [viewer]);

  if (!attachments.length) {
    return null;
  }

  async function openViewer(attachment) {
    const attachmentId = attachment.id || getAttachmentName(attachment);
    setLoadingAttachmentId(attachmentId);
    setErrorMessage('');

    try {
      const fileUrl = getAttachmentUrl(attachment);
      const blob = await fetchAuthenticatedFile(fileUrl);
      const blobUrl = URL.createObjectURL(blob);
      const mimeType = inferAttachmentMimeType(attachment) || blob.type;

      setViewer((currentViewer) => {
        if (currentViewer?.blobUrl) {
          URL.revokeObjectURL(currentViewer.blobUrl);
        }

        return {
          blob,
          blobUrl,
          fileName: getAttachmentName(attachment),
          mimeType,
        };
      });
    } catch (error) {
      setErrorMessage(error.message || 'امکان نمایش فایل وجود ندارد.');
    } finally {
      setLoadingAttachmentId('');
    }
  }

  async function handleDownload(attachment) {
    const attachmentId = attachment.id || getAttachmentName(attachment);
    setLoadingAttachmentId(attachmentId);
    setErrorMessage('');

    try {
      await downloadAuthenticatedFile(
        getAttachmentUrl(attachment),
        getAttachmentName(attachment)
      );
    } catch (error) {
      setErrorMessage(error.message || 'دانلود فایل انجام نشد.');
    } finally {
      setLoadingAttachmentId('');
    }
  }

  function closeViewer() {
    setViewer((currentViewer) => {
      if (currentViewer?.blobUrl) {
        URL.revokeObjectURL(currentViewer.blobUrl);
      }

      return null;
    });
  }

  return (
    <>
      <div className={`flex flex-col gap-2 ${className}`}>
        {title && (
          <p className={`text-[11px] font-semibold ${isEmbedded ? 'text-slate-600' : 'text-slate-800'}`}>
            {title}
          </p>
        )}

        {attachments.map((attachment, index) => (
          <AttachmentItem
            key={attachment.id || index}
            attachment={attachment}
            index={index}
            isEmbedded={isEmbedded}
            isLoading={loadingAttachmentId === (attachment.id || getAttachmentName(attachment))}
            onDownload={handleDownload}
            onView={openViewer}
          />
        ))}

        {errorMessage && (
          <p className="text-[10px] font-semibold text-red-600">
            {errorMessage}
          </p>
        )}
      </div>

      <FileViewerModal viewer={viewer} onClose={closeViewer} />
    </>
  );
}

export default FileAttachments;
